import { alpha, SxProps, Theme, useTheme } from "@mui/material";
import {
  areaElementClasses,
  axisClasses,
  chartsAxisHighlightClasses,
  chartsGridClasses,
  chartsTooltipClasses,
  LineChart,
  lineElementClasses,
  markElementClasses,
  useDrawingArea,
} from "@mui/x-charts";
import { numberKFormat } from "~/lib/number-k-formatting";
import { MetricsEntries } from "~/lib/arrow";
import { useDailyPriorityFindingCounts } from "../PriorityFindings/useDailyPriorityFindingCounts";

export type FindingsDailyCountChartProps = {
  entityMrn: string;
  numOfDays?: number;
  entries?: MetricsEntries;
};

export function FindingsDailyCountChart({
  entityMrn,
  numOfDays = 28,
  entries,
}: FindingsDailyCountChartProps) {
  const theme = useTheme();

  const { data } = useDailyPriorityFindingCounts({
    entityMrn,
    numOfDays,
    entries,
  });

  const xAxisData = data.map(({ date }) => date);
  const dailyData = data.map(({ count }) => count);
  const firstNonNullIndex = dailyData.findIndex((total) => total !== null);
  const lastNonNullIndex = dailyData.findLastIndex((total) => total !== null);
  const trendData = dailyData.map((total, index) => {
    // trendData is same as dailyData but only the first and last filled points
    if (index === firstNonNullIndex || index === lastNonNullIndex) {
      return total;
    }
    // The other points between first and last are not drawn
    return null;
  });

  const lineChartStyle: SxProps<Theme> = {
    [`& .${lineElementClasses.series}-trendLine`]: {
      strokeDasharray: "4 4",
      strokeLinecap: "round",
    },
    [`& .${areaElementClasses.series}-dailyArea`]: {
      fill: "url(#fill-gradient)",
    },
    [`& .${chartsAxisHighlightClasses.root}`]: {
      stroke: alpha(theme.palette.primary.light, 0.4),
    },
    [`& .${axisClasses.directionX} .${axisClasses.tickContainer}`]: {
      [`& .${axisClasses.tickLabel}`]: {
        transform: "translateY(4px)",
      },
      [`&:first-of-type .${axisClasses.tickLabel}`]: {
        textAnchor: "start",
      },
    },
    [`& .${axisClasses.directionY} .${axisClasses.tickContainer}`]: {
      [`& .${axisClasses.tickLabel}`]: {
        transform: "translateX(-4px)",
      },
    },
    [`& .${chartsGridClasses.horizontalLine}`]: {
      stroke: alpha(theme.palette.divider, 0.04),

      "&:first-of-type": {
        stroke: alpha(theme.palette.divider, 0.16),
      },
    },
    [`& .${markElementClasses.root}`]: {
      [`&.${markElementClasses.series}-trendLine`]: {
        stroke: theme.palette.primary.light,
      },
    },
  };

  const tickLabelStyle = {
    fontSize: 12,
    fontWeight: 700,
    fill: alpha(theme.palette.text.primary, 0.3),
  };

  const yMin = 0;
  const yMax = dailyData.filter((v) => v && v > 0).length > 0 ? undefined : 1;

  return (
    <LineChart
      sx={lineChartStyle}
      xAxis={[
        {
          data: xAxisData,
          scaleType: "point",
          disableTicks: true,
          disableLine: true,
          tickLabelStyle: { ...tickLabelStyle, textAnchor: "end" },
          tickLabelInterval(_, index) {
            // only show first and last labels
            return index === 0 || index === xAxisData.length - 1;
          },
          valueFormatter: (date: Date, context) => {
            const index = xAxisData.indexOf(date);
            const total = xAxisData.length;
            if (context.location === "tick") {
              if (index === 0) {
                return `${total}d ago`;
              }
              if (index === total - 1) {
                return "Now";
              }
            }

            return date.toISOString().substring(0, 10);
          },
        },
      ]}
      series={[
        {
          data: dailyData,
          color: theme.palette.primary.light,
          id: "dailyLine",
          showMark: false,
          connectNulls: true,
          valueFormatter: (value) =>
            value === null ? "Unavailable" : String(value),
        },
        {
          data: trendData,
          color: alpha(theme.palette.primary.main, 0.4),
          id: "trendLine",
          showMark: true,
          connectNulls: true,
          valueFormatter: () => null,
        },
        {
          data: dailyData,
          color: theme.palette.primary.light,
          id: "dailyArea",
          area: true,
          showMark: false,
          connectNulls: true,
          valueFormatter: () => null,
        },
      ]}
      yAxis={[
        {
          disableLine: true,
          disableTicks: true,
          min: yMin,
          max: yMax,
          width: 50,
          tickLabelStyle,
          tickLabelInterval(count: number) {
            // only show whole numbers
            return count % 1 === 0;
          },
          valueFormatter: (count: number) => {
            return numberKFormat.format(count);
          },
        },
      ]}
      height={208}
      grid={{ horizontal: true }}
      margin={{ top: 20, left: 0, bottom: 0, right: 6 }}
      slotProps={{
        tooltip: {
          sx: {
            [`&.${chartsTooltipClasses.root}`]: {
              [`.${chartsTooltipClasses.table} caption`]: {
                color: "text.secondary",
                fontWeight: 400,
                fontSize: 14,
              },
              [`.${chartsTooltipClasses.cell}`]: {
                color: "text.primary",
                fontWeight: 400,
                fontSize: 14,
              },
              [`.${chartsTooltipClasses.labelCell}`]: {
                display: "none",
              },
            },
          },
        },
      }}
      children={<FillGradient id="fill-gradient" />}
    />
  );
}

function FillGradient({ id, color }: { id: string; color?: string }) {
  const theme = useTheme();
  const { top, height, bottom } = useDrawingArea();
  const svgHeight = top + bottom + height;
  const stopColor = color || theme.palette.primary.main;
  return (
    <defs>
      <linearGradient
        id={id}
        x1={0}
        x2={0}
        y1={0}
        y2={`${svgHeight}px`}
        gradientUnits="userSpaceOnUse"
      >
        <stop stopOpacity="0.2" stopColor={stopColor} offset="0%"></stop>
        <stop stopOpacity="0" stopColor={stopColor} offset="85%"></stop>
      </linearGradient>
    </defs>
  );
}
